import React from 'react';
import { CreditCard, LogIn, Menu, UserPlus, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { APP_NAME } from '../../constants/app';
import { useViewport } from '../../contexts/ViewportContext';
import { useStatusBarInfo } from '../../hooks/useStatusBarInfo';

interface NavigationProps {
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (open: boolean) => void;
  onAuthClick: (mode: 'login' | 'signup') => void;
}

export function Navigation({
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  onAuthClick,
}: NavigationProps) {
  const { isMobile } = useViewport();
  const { isNativeApp, statusBarHeight } = useStatusBarInfo();

  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing-section');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-gray-900/50 backdrop-blur-lg border-b border-gray-800"
      style={{
        paddingTop: isMobile && isNativeApp ? `${statusBarHeight}px` : '0',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="flex items-center justify-between"
          style={{
            height: isMobile && isNativeApp ? '60px' : '80px', // h-20 is 5rem = 80px
          }}
        >
          <div className="flex items-center gap-2">
            <img src="/logo-icon-white.svg" alt="Zen Logo" className="h-10 w-auto mt-2" />
            <span className="text-3xl font-bold text-white font-['Sora']">{APP_NAME}</span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={scrollToPricing}
              className="text-gray-300 hover:text-white px-4 py-2 rounded-lg text-base font-medium flex items-center gap-2 hover:bg-gray-800 transition-colors"
            >
              <CreditCard size={20} />
              <span>Pricing</span>
            </button>
            <button
              onClick={() => onAuthClick('login')}
              className="text-gray-300 hover:text-white px-4 py-2 rounded-lg text-base font-medium flex items-center gap-2 hover:bg-gray-800 transition-colors"
            >
              <LogIn size={20} />
              <span>Sign In</span>
            </button>
            <button
              onClick={() => onAuthClick('signup')}
              className="group relative bg-gradient-to-r from-blue-500 via-blue-400 to-blue-500 text-white px-6 py-2 rounded-lg text-base font-medium flex items-center gap-2 shadow-[0_0_20px_rgba(59,91,219,0.2)] hover:shadow-[0_0_25px_rgba(59,91,219,0.4)] transition-all duration-500 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500" />
              <UserPlus size={20} className="relative z-10" />
              <span className="relative z-10">Sign Up</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={cn(
            'md:hidden',
            'absolute left-0 right-0 p-4 bg-gray-900/95 backdrop-blur-lg border-b border-gray-800',
            'transform transition-all duration-300 ease-in-out',
            isMobileMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
          )}
          style={{
            top: isMobile && isNativeApp ? `calc(60px + ${statusBarHeight}px)` : '80px', // top-20 = 5rem = 80px
          }}
        >
          <div className="flex flex-col gap-2">
            <button
              onClick={scrollToPricing}
              className="w-full text-left text-gray-300 hover:text-white px-4 py-3 rounded-lg text-base font-medium flex items-center gap-2 hover:bg-gray-800 transition-colors"
            >
              <CreditCard size={20} />
              <span>Pricing</span>
            </button>
            <button
              onClick={() => onAuthClick('login')}
              className="w-full text-left text-gray-300 hover:text-white px-4 py-3 rounded-lg text-base font-medium flex items-center gap-2 hover:bg-gray-800 transition-colors"
            >
              <LogIn size={20} />
              <span>Sign In</span>
            </button>
            <button
              onClick={() => onAuthClick('signup')}
              className="group relative w-full bg-gradient-to-r from-blue-500 via-blue-400 to-blue-500 text-white px-4 py-3 rounded-lg text-base font-medium flex items-center gap-2 shadow-[0_0_20px_rgba(59,91,219,0.2)] hover:shadow-[0_0_25px_rgba(59,91,219,0.4)] transition-all duration-500 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500" />
              <UserPlus size={20} className="relative z-10" />
              <span className="relative z-10">Sign Up</span>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
